import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { SparklesIcon, CheckCircleIcon } from '@heroicons/react/24/solid'; // Assuming Heroicons installed

// Adjust this path to your UrlConfig.js file
import UrlConfig from '../../data/UrlConfig';

// IMPORTANT: Use the actual cover image for Bashar's Metaphysics Compilation Book
// For now, I'm using a placeholder from your previous examples.
import basharBookCover from '../../assets/images/jdm-tecnicas/tapa.png'; 

const API_URLS = UrlConfig;

const BasharBookSubscriptionPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(''); // For success messages from backend
  const [error, setError] = useState('');     // For error messages from backend or frontend
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false); // To control frontend success state

  const { ref, inView } = useInView({
    threshold: 0.1, // Trigger when 10% of the element is visible
    triggerOnce: true, // Animate only once
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setError('');

    // Basic frontend validation
    if (!email.trim()) {
      setError('Please enter your email address.');
      setIsLoading(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      setIsLoading(false);
      return;
    }

    try {
      // Your 'add_lead' logic goes here, tailored for the book subscription
      const response = await fetch(API_URLS.leads_action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          nick: 'Anonymous', // Since we only ask for email here, use a default name
          page: 'BasharBookSubscriptionPage',
          section: 'book-launch-subscription-form',
          status: 'BasharBookLaunchInterest',
          newsletter: ['bashar-book-launch'], // Specific tag for book launch interest
          message: 'Interested in Bashar\'s Metaphysics Compilation Book launch.', // Default message
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Thank you! We will notify you when the book is launched.');
        setEmail('');
        setSubmitted(true);
      } else {
        throw new Error(data.message || 'An error occurred while processing your request.');
      }
    } catch (err) {
      console.error("Bashar Book Subscription Error:", err);
      setError(err.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col items-center py-12 px-4">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95"></div>

      <main 
        ref={ref}
        className={`relative z-10 w-full max-w-2xl mx-auto
          flex flex-col items-center text-center
          transition-all duration-700 ease-out
          ${inView ? "animate-fade-in" : "opacity-0 translate-y-8"}
        `}
      >
        {/* Book Information Section (Featured Style) */}
        <div className="relative w-full mb-8 rounded-xl overflow-hidden group">
          {/* Animated pseudo-element for the featured border/glow effect */}
          <div className="absolute -inset-1 rounded-xl bg-gradient-to-r from-sky-400 via-purple-500 to-pink-500 opacity-70 blur group-hover:opacity-100 group-hover:duration-200 animate-spin-slow"></div>
          
          <div className="relative bg-black/50 p-6 sm:p-8 rounded-xl flex flex-col items-center justify-center transition-colors duration-300 group-hover:bg-black/70">
            {/* Book Cover Image */}
            <div className="w-48 h-auto mb-6 shadow-xl rounded-md border border-gray-700">
              <img
                src={basharBookCover} // Use your specific Bashar book cover image here
                alt="Bashar's Metaphysics Compilation Book Cover"
                className="w-full h-full object-contain rounded-md"
              />
            </div>

            {/* Book Title */}
            <h1 className="text-3xl sm:text-4xl font-extrabold mb-2 leading-tight">
              Bashar's Metaphysics Compilation Book
            </h1>
            {/* Book Subtitle */}
            <p className="text-lg sm:text-xl text-gray-300 mb-6">
              A book every fan of Bashar should have
            </p>
          </div>
        </div>

        {/* Subscription Form Section */}
        <div className="w-full max-w-md bg-black/30 border border-gray-700 shadow-lg shadow-gray-700/30 rounded-xl p-6 sm:p-8">
          {submitted ? (
            <div className="text-center text-green-400 p-6 bg-green-900/30 border border-green-700 rounded-lg">
              <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-xl font-semibold">{message}</p>
              <p className="text-lg">We've got your interest recorded! Stay tuned for the book launch.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col space-y-6">
              <h2 className="text-2xl font-semibold mb-2 text-sky-400">Get Notified When It Launches!</h2>
              <p className="text-gray-400 -mt-3">
                Enter your email below to receive updates and be among the first to know when the book is officially released.
              </p>

              <div>
                <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2 sr-only">
                  Your Email
                </label> {/* sr-only hides label visually but keeps for screen readers */}
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 rounded-md bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder="your.email@example.com"
                  required
                  disabled={isLoading}
                  aria-label="Email address for book launch notification"
                />
              </div>

              {(error || message) && (
                  <p className={`text-sm text-center ${error ? 'text-red-400' : 'text-green-400'}`}>
                      {error || message}
                  </p>
              )}

              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold text-lg
                           hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500
                           transition-all duration-300 ease-in-out shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                    <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Subscribing...
                    </>
                ) : (
                    <>
                        Subscribe for Launch <SparklesIcon className="w-5 h-5" />
                    </>
                )}
              </button>
            </form>
          )}
        </div>
      </main>

      {/* Footer consistent with other pages */}
      <footer className="relative z-10 text-center py-6 mt-12 text-xs text-gray-600">
        © {new Date().getFullYear()} Gus • Aguiar | All rights reserved.
      </footer>
    </div>
  );
};

export default BasharBookSubscriptionPage;